import moment from 'moment';
import {
  chain, range, sum, sumBy,
} from 'lodash';

const MONTHS_TO_SHOW = 6;
const MONTHS_THRESHOLD = 2;

function buildMonthChart(data, maxMonths = MONTHS_TO_SHOW) {
  return chain(range(0, maxMonths))
    .map((month) => {
      const current = moment().subtract(month, 'month');
      const dataKey = current.format('YYYYMM');
      const description = current.format('MMM');
      return { dataKey, description, value: data[dataKey] || 0 };
    })
    .sortBy('dataKey')
    .value();
}

function buildKeyValueChart(data) {
  return chain(Object.keys(data))
    .map(key => ({ dataKey: key, description: key, value: data[key] || 0 }))
    .sortBy('value')
    .value();
}

function buildTimeframeChart(data) {
  const dates = Object.keys(data);
  if (!dates || !dates.length) {
    return [];
  }

  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];

  const differenceInMonths = moment(lastDate)
    .diff(moment(firstDate), 'months', true);

  if (differenceInMonths <= MONTHS_THRESHOLD) {
    return chain(dates)
      .map((key) => {
        const dateDescription = moment(key).format('DD/MM/YYYY');
        return { dataKey: key, description: dateDescription, value: data[key] || 0 };
      })
      .sortBy('dataKey')
      .value();
  }

  const dataGroupedByMonth = dates.map(key => ({
    month: moment(key).format('YYYYMM'),
    monthDescription: moment(key).format('MMM'),
    value: data[key],
  }));

  return chain(dataGroupedByMonth)
    .groupBy('month')
    .map((valuesForMonth) => {
      const groupedValue = {
        month: valuesForMonth[0].month,
        value: sumBy(valuesForMonth, 'value'),
      };
      return {
        dataKey: groupedValue.month,
        description: valuesForMonth[0].monthDescription,
        value: groupedValue.value || 0,
      };
    })
    .value();
}

function buildCategoryEnumChart(data, enumOptions) {
  if (!enumOptions) {
    return Object.keys(data)
      .map(key => ({ dataKey: key, description: key, value: data[key] || 0 }));
  }
  return Object.keys(data)
    .map((key) => {
      const keyDescription = enumOptions[key] ? enumOptions[key].description : key;
      return ({ dataKey: key, description: keyDescription, value: data[key] || 0 });
    });
}

function toEchartData(data, key, value) {
  return data.map(d => [d[key], d[value], d.dataKey]);
}

function sumData(data) {
  const values = Object.keys(data).map(key => data[key]);
  return sum(values);
}

function getColorFromDataIndex(dataIndex, colors) {
  return colors[dataIndex % colors.length];
}

export {
  buildMonthChart,
  toEchartData,
  sumData,
  buildCategoryEnumChart,
  buildTimeframeChart,
  buildKeyValueChart,
  getColorFromDataIndex,
};
